import {Component, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import {MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {pairwise, startWith, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

import {
    FormulaOperations,
    LOGIN_CREDENTIALS_FORMULA_TYPES,
    LoginCredentialsFormula,
    LoginCredentialsFormulaArgument,
} from '@app/views/admin/application-settings/components/login-credentials/entity-login-credentials/entity-login-credentials.component';

@Component({
    selector: 'ngc-formula-dialog',
    templateUrl: './formula-dialog.component.html',
    styleUrls: ['./formula-dialog.component.scss']
})
export class FormulaDialogComponent implements OnInit, OnDestroy {

    @Input() item;
    @Input() formulaOperations: LoginCredentialsFormula[];
    @Input() attributes: string[];
    @Input() selectedFormulaName: string;
    @Input() selectedAttributeName: string;
    @Input() selectedFormulaArguments: any[];

    public attributeCtrl: UntypedFormControl = new UntypedFormControl('', Validators.required);
    public formulaCtrl: UntypedFormControl = new UntypedFormControl('');
    public formulaFormGroup: UntypedFormGroup = new UntypedFormGroup({
        attributeCtrl: this.attributeCtrl,
        formulaCtrl: this.formulaCtrl
    });
    private unsubscribe$ = new Subject<void>();

    readonly LOGIN_CREDENTIALS_FORMULA_TYPES = LOGIN_CREDENTIALS_FORMULA_TYPES;

    @HostListener('document:keydown.enter', ['$event'])
    onEnterKeydownHandler(event: KeyboardEvent) {
        this.onSubmit();
        event.preventDefault();
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKeydownHandler(event: KeyboardEvent) {
        this.onCancel();
    }

    constructor(public dialogRef: MatDialogRef<FormulaDialogComponent>) {
    }

    ngOnInit() {
        if (this.selectedAttributeName) {
            this.formulaFormGroup.controls.attributeCtrl.setValue(`{${this.selectedAttributeName}}`);
        }
        const formulaOperation = this.formulaOperations
            .find((formula: LoginCredentialsFormula) => formula.title === this.selectedFormulaName);
        this.formulaFormGroup.controls.formulaCtrl.setValue(formulaOperation);

        if (formulaOperation) {
            formulaOperation.argumentsList.forEach((argument: LoginCredentialsFormulaArgument, i: number) => {
                this.formulaFormGroup.addControl(
                    argument.argumentFieldName, new UntypedFormControl(this.selectedFormulaArguments[i], Validators.required)
                );
            });
        }

        this.formulaFormGroup.controls.formulaCtrl.valueChanges.pipe(
            takeUntil(this.unsubscribe$),
            startWith(this.formulaFormGroup.controls.formulaCtrl.value),
            pairwise()
        ).subscribe(
            ([oldValue, newValue]: [oldValue: LoginCredentialsFormula, newValue: LoginCredentialsFormula]) => {
                if (oldValue) {
                    oldValue.argumentsList.forEach((argument: LoginCredentialsFormulaArgument) => {
                        this.formulaFormGroup.removeControl(argument.argumentFieldName);
                    });
                }
                newValue.argumentsList.forEach((argument: LoginCredentialsFormulaArgument) => {
                    this.formulaFormGroup.addControl(argument.argumentFieldName, new UntypedFormControl('', Validators.required));
                });
            }
        );
    }

    onSubmit() {
        this.close(true);
    }

    private close(save: boolean = false) {
        let formula: string;
        if (save) {
            // this.dialogRef.close(result ? this.selectedCheckboxList : null);
            if (this.formulaFormGroup.controls.formulaCtrl.value) {
                formula = '{' + this.formulaFormGroup.controls.formulaCtrl.value.title
                    + '('
                    + this.formulaFormGroup.controls.attributeCtrl.value.slice(1, -1);
                this.formulaFormGroup.controls.formulaCtrl.value.argumentsList.forEach((argument: LoginCredentialsFormulaArgument) => {
                    formula = formula + ',' + this.formulaFormGroup.controls[argument.argumentFieldName].value;
                });
                formula = formula + ')}';
            } else {
                formula = this.formulaFormGroup.controls.attributeCtrl.value;
            }
        }
        this.dialogRef.close(formula);
    }

    onCancel() {
        this.close(false);
    }

    public formulaComparisonFunction(optionOne, optionTwo): boolean {
        return optionOne.title === optionTwo.title;
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
